import {
  createContext,
  type PropsWithChildren,
  useMemo,
  useSyncExternalStore,
} from 'react';
import { de, enGB, Locale } from 'date-fns/locale';

export type Languages = Record<string, { locale: Locale; name: string }>;

export interface I18nContextType {
  resolvedLanguage: string;
  t: (key: string, options?: Record<string, unknown>) => string;
  languages?: Languages;
}

export interface I18nAdapter {
  t: I18nContextType['t'];
  getResolvedLanguage: () => string;
  subscribe: (onStoreChange: () => void) => () => void;
}

export const I18nContext = createContext<I18nContextType | null>(null);

const FALLBACK_LANGUAGES = {
  de: {
    name: 'Deutsch',
    locale: de,
  },
  en: {
    name: 'English',
    locale: enGB,
  },
} satisfies Languages;

export const I18nProvider = ({
  children,
  i18n,
  languages,
}: PropsWithChildren<{ i18n: I18nAdapter; languages?: Languages }>) => {
  const resolvedLanguage = useSyncExternalStore(
    i18n.subscribe,
    i18n.getResolvedLanguage,
    i18n.getResolvedLanguage,
  );

  const value = useMemo<I18nContextType>(() => {
    return {
      resolvedLanguage,
      t: typeof i18n.t === 'function' ? i18n.t : (key) => key,
      languages: languages ?? FALLBACK_LANGUAGES,
    };
  }, [i18n, languages, resolvedLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};
